import React, { FC, useId } from 'react';

import {
	EvCaptionDefault,
	EvFlexColumn,
	EvFlexRow,
	EvInfoTooltip,
	EvLabelDefault,
	EvTextArea,
	EvTextInput,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import {
	LABEL_MANAGEMENT_PANEL_DESCRIPTION_INPUT_DAID,
	LABEL_MANAGEMENT_PANEL_DISPLAY_NAME_INPUT_DAID,
	LABEL_MANAGEMENT_PANEL_KEY_DISPLAY_DAID,
	LABEL_MANAGEMENT_PANEL_KEY_INPUT_DAID
} from 'data-automation-ids/LabelManagementAutomationIds';

import { LABEL_PANEL_VALIDATION } from '../../LabelPanel.utils';

import { DESCRIPTION_TEXTAREA_ROWS } from './LabelDetailsSection.constants';
import { LABEL_DETAILS_LABELS } from './LabelDetailsSection.labels';
import { LabelDetailsSectionProps } from './LabelDetailsSection.types';

import './LabelDetailsSection.scss';

export const LabelDetailsSection: FC<LabelDetailsSectionProps> = ({
	displayName,
	description,
	labelKey,
	isKeyEditable,
	keyError,
	onDisplayNameChange,
	onDescriptionChange,
	onKeyChange,
	displayNameError,
	descriptionError
}) => {
	const displayNameId = useId();
	const keyInputId = useId();

	return (
		<EvFlexColumn gap="LARGE">
			{isKeyEditable && (
				<EvFlexColumn gap="SMALL">
					<EvFlexRow align="CENTER" gap="SMALL">
						<EvLabelDefault as="label" htmlFor={keyInputId}>
							{LABEL_DETAILS_LABELS.KEY_LABEL}
						</EvLabelDefault>
						<EvInfoTooltip title={LABEL_DETAILS_LABELS.KEY_LABEL}>
							{LABEL_DETAILS_LABELS.KEY_TOOLTIP}
						</EvInfoTooltip>
					</EvFlexRow>
					<EvTextInput
						id={keyInputId}
						value={labelKey ?? ''}
						onChange={onKeyChange}
						isRequired
						dataAutomationId={LABEL_MANAGEMENT_PANEL_KEY_INPUT_DAID}
					/>
					{keyError && <EvCaptionDefault color={TEXT_COLOR.RED}>{keyError}</EvCaptionDefault>}
				</EvFlexColumn>
			)}

			{!isKeyEditable && labelKey && (
				<EvFlexColumn gap="SMALL">
					<EvLabelDefault>{LABEL_DETAILS_LABELS.KEY_DISPLAY_LABEL}</EvLabelDefault>
					<EvCaptionDefault
						color={TEXT_COLOR.CONTENT_3}
						dataAutomationId={LABEL_MANAGEMENT_PANEL_KEY_DISPLAY_DAID}
					>
						{labelKey}
					</EvCaptionDefault>
				</EvFlexColumn>
			)}

			<EvFlexColumn gap="SMALL">
				<EvFlexRow align="CENTER" gap="SMALL">
					<EvLabelDefault as="label" htmlFor={displayNameId}>
						{LABEL_DETAILS_LABELS.DISPLAY_NAME_LABEL}
					</EvLabelDefault>
					<EvInfoTooltip title={LABEL_DETAILS_LABELS.DISPLAY_NAME_LABEL}>
						{LABEL_DETAILS_LABELS.DISPLAY_NAME_TOOLTIP}
					</EvInfoTooltip>
				</EvFlexRow>
				<EvTextInput
					id={displayNameId}
					value={displayName}
					onChange={onDisplayNameChange}
					isRequired
					dataAutomationId={LABEL_MANAGEMENT_PANEL_DISPLAY_NAME_INPUT_DAID}
				/>
				{displayNameError && (
					<EvCaptionDefault color={TEXT_COLOR.RED}>{displayNameError}</EvCaptionDefault>
				)}
			</EvFlexColumn>

			<EvTextArea
				label={LABEL_DETAILS_LABELS.DESCRIPTION_LABEL}
				tooltip={LABEL_DETAILS_LABELS.DESCRIPTION_TOOLTIP}
				value={description}
				onChange={(e) => onDescriptionChange(e.target.value)}
				placeholder={LABEL_DETAILS_LABELS.DESCRIPTION_PLACEHOLDER}
				maxLength={LABEL_PANEL_VALIDATION.DESCRIPTION_MAX_LENGTH}
				dataAutomationId={LABEL_MANAGEMENT_PANEL_DESCRIPTION_INPUT_DAID}
				error={descriptionError}
				rows={DESCRIPTION_TEXTAREA_ROWS}
			/>
		</EvFlexColumn>
	);
};
